import { createMemo, createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { useSearchParams } from '@solidjs/router';
import { WrappedImage } from '../../components/image-component/wrapped-image';
import { ClientsProps, ClientType } from './clients.types';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import { breakpoints } from '../../style/theme';
import clsx from 'clsx';
import { Heading } from '../heading/heading';
import { CategoryFilter } from '../category-filter/category-filter';

export const ClientSelector = (props: ClientsProps) => {
    const { clients = [], showTitles, animateOpacity, controlLayout, hideFilter, clientCategoriesFromHook = [] } = props;
    const { viewport, siteInfo, translate } = useAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    // The URL is the single source of truth, so back/forward re-filters the grid.
    const selectedFilter = () => searchParams.category || undefined;

    const handleFilterClick = (slug: string | undefined) => {
        setSearchParams({ category: slug });
    };
    const isDesktop = () => viewport.width >= breakpoints.desktop && viewport.width < breakpoints.desktopXL;
    const isExternalUrl = (url: string) => {
        if (!url || !url.startsWith('http')) {
            return false;
        }
        try {
            const urlHost = new URL(url).hostname;
            const siteHost = new URL(siteInfo.baseurl).hostname;
            return urlHost !== siteHost;
        } catch {
            return false;
        }
    };
    const getLinkProps = (client: ClientType) => {
        if (client.url?.url) {
            return { href: client.url.url, target: isExternalUrl(client.url.url) ? '_blank' : '_self' };
        }
        if (client.hasPostContent && client.permalink) {
            return { href: client.permalink, target: '_self' };
        }
        return { href: client.client_link, target: client.client_link && isExternalUrl(client.client_link) ? '_blank' : '_self' };
    };

    const getAngle = () => {
        const randomValue = Math.random();
        let angle = 0;

        if (randomValue < 0.33) {
            angle = Math.random() * -5 - 2;
        } else if (randomValue > 0.66) {
            angle = Math.random() * 5 + 2;
        }

        return angle;
    };

    const opacityById = new Map(clients.map((c) => [c.id, createSignal(Math.random() * (1 - 0.6) + 0.6)]));
    const hoveredById = new Map(clients.map((c) => [c.id, createSignal(false)]));
    const intervals: NodeJS.Timeout[] = [];

    onMount(() => {
        clients.forEach((client) => {
            const opacitySignal = opacityById.get(client.id);
            const hoveredSignal = hoveredById.get(client.id);
            if (!opacitySignal || !hoveredSignal) return;
            const [, setOpacity] = opacitySignal;
            const [hovered] = hoveredSignal;

            const animateOpacity = () => {
                if (hovered()) return;

                setOpacity(1);
                setTimeout(
                    () => {
                        if (hovered()) return;
                        setOpacity(Math.random() * 0.3 + 0.4);
                    },
                    Math.random() * 3000 + 2000,
                );
            };

            const interval = setInterval(animateOpacity, Math.random() * 4000 + 6000);
            intervals.push(interval);

            setTimeout(animateOpacity, Math.random() * 3500 + 2000);
        });
    });

    onCleanup(() => {
        intervals.forEach(clearInterval);
    });

    const getMarginClasses = (index: number) => {
        const forAll = clsx({
            'aspect-square': !showTitles,
            'tablet:-mt-12 tablet:mb-12': index % 3 === 1 && animateOpacity && !isDesktop(),
            'bigTablet:-mt-12 bigTablet:mb-12': index % 3 === 1 && animateOpacity && !isDesktop(),
        });

        if (!controlLayout.isControlled) {
            return (
                forAll +
                ' ' +
                clsx({
                    'laptop:-mt-12 laptop:mb-12': index % 3 === 1 && animateOpacity && !isDesktop(),
                    'desktop:-mt-12 desktop:mb-12': (index % 4 === 1 || index % 4 === 3) && animateOpacity && isDesktop(),
                    'desktopXL:-mt-12 desktopXL:mb-12': (index % 5 === 1 || index % 5 === 3) && animateOpacity && !isDesktop(),
                    'desktop:-mt-0 desktop:mb-0': (index % 4 === 0 || index % 4 === 2) && animateOpacity && isDesktop(),
                    'desktopXL:mt-0 desktopXL:mb-0': (index % 5 === 0 || index % 5 === 2 || index % 5 === 4) && animateOpacity && !isDesktop(),
                })
            );
        }

        if (controlLayout.isControlled && controlLayout.rowLength === 3) {
            return (
                forAll +
                ' ' +
                clsx({
                    'laptop:-mt-12 laptop:mb-12': index % 3 === 1 && animateOpacity,
                })
            );
        }

        if (controlLayout.isControlled && controlLayout.rowLength === 4) {
            return (
                forAll +
                ' ' +
                clsx({
                    'desktop:-mt-12 desktop:mb-12': (index % 4 === 1 || index % 4 === 3) && animateOpacity,
                    'desktop:-mt-0 desktop:mb-0': (index % 4 === 0 || index % 4 === 2) && animateOpacity,
                })
            );
        }
    };

    const normalizedClients = createMemo(() =>
        clients
            // Skip clients whose __reference didn't resolve (e.g., cerberus-unpublished)
            .filter((client) => client.client_name || client.featured_image)
            .map((client) => ({
                ...client,
                priority: typeof client.priority === 'number' ? client.priority : 0,
                /* eslint-disable camelcase */
                client_priority:
                    typeof client.client_priority === 'string'
                        ? parseInt(client.client_priority, 10) || 0
                        : typeof client.client_priority === 'number'
                            ? client.client_priority
                            : 0,
                /* eslint-enable camelcase */
            })),
    );

    const sortedClients = createMemo(() =>
        [...normalizedClients()].sort((a, b) => {
            if (a.priority !== b.priority) return b.priority - a.priority;
            return b.client_priority - a.client_priority;
        }),
    );

    const filteredClients = createMemo(() => {
        const filter = selectedFilter();
        if (hideFilter || !filter) {
            return sortedClients();
        }
        return sortedClients().filter((client) => (client.categories || []).some((cat) => cat.slug === filter));
    });

    // Only offer categories that some rendered client actually has. The hook injects
    // every category site-wide, but the block may render a subset (clients/excludedClients).
    const availableCategories = createMemo(() => {
        const present = new Set(sortedClients().flatMap((client) => (client.categories ?? []).map((cat) => cat.slug)));
        return clientCategoriesFromHook.filter((category) => present.has(category.slug));
    });

    return (
        <div class="container mx-auto">
            <Show when={!hideFilter && availableCategories().length > 0}>
                <div class="mt-s tablet:mt-0 tablet:pb-xl">
                    <CategoryFilter categories={availableCategories()} selectedSlug={selectedFilter()} onSelect={handleFilterClick} />
                </div>
                <p class="sr-only" role="status">
                    {`${translate('found')} ${filteredClients().length} ${translate('hits')}`}
                </p>
            </Show>
            <Show
                when={filteredClients().length > 0}
                fallback={<p class="text-paragraphM py-l text-center">{translate('no-results')}</p>}
            >
                <ul
                    class="tablet:grid-cols-3 bigTablet:grid-cols-3 laptop:grid-cols-3 tablet:gap-12 grid grid-cols-2 gap-6"
                    classList={{
                        'tablet:gap-x-12 gap-y-4 tablet:gap-y-2': showTitles,
                        'desktop:grid-cols-4 desktopXL:grid-cols-5': !controlLayout.isControlled,
                        'desktop:grid-cols-4': controlLayout.isControlled && controlLayout.rowLength === 4,
                    }}
                >
                    <For each={filteredClients()}>
                        {(client: ClientType, index) => {
                            const angle = getAngle();
                            const opacitySignal = opacityById.get(client.id);
                            const hoveredSignal = hoveredById.get(client.id);
                            if (!opacitySignal || !hoveredSignal) return null;
                            const [opacity] = opacitySignal;
                            const [hovered, setHovered] = hoveredSignal;
                            const { href, target } = getLinkProps(client);

                            return (
                                <li class={'tablet:pb-4 desktop:pb-6 h-full w-full ' + getMarginClasses(index())}>
                                    <a
                                        href={href}
                                        target={target}
                                        rel="noopener noreferrer"
                                        class="duration-400 group block transition-all ease-in-out hover:animate-none hover:opacity-100 focus:animate-none focus-visible:opacity-100"
                                        aria-label={client.client_name}
                                        style={{ opacity: hovered() ? 1 : opacity() }}
                                        onMouseEnter={() => setHovered(true)}
                                        onMouseLeave={() => setHovered(false)}
                                    >
                                        <div
                                            class="rounded-m tablet:mb-s p-m duration-400 mb-2 flex aspect-square items-center justify-center bg-white transition-all ease-in-out hover:scale-110 focus:scale-110 group-hover:blur-none group-focus:blur-none"
                                            style={{
                                                rotate: animateOpacity && !hovered() ? `${angle}deg` : '',
                                            }}
                                        >
                                            <Show
                                                when={client.featured_image}
                                                fallback={<img src="/assets/twofour-logo.png" alt={client.client_name} class="h-full w-full object-contain" />}
                                            >
                                                <WrappedImage
                                                    width={300}
                                                    renderWidth={'100%'}
                                                    renderHeight={'100%'}
                                                    height={'auto'}
                                                    aspectRatio={'clients'}
                                                    image={client.featured_image}
                                                    classes="object-contain w-full h-full "
                                                    roundCorners="none"
                                                />
                                            </Show>
                                        </div>
                                        <Show when={showTitles}>
                                            <Heading tag="h2" classes="text-center text-paragraphS font-poppins" disableMargins>
                                                {client.client_name}
                                            </Heading>
                                        </Show>
                                    </a>
                                </li>
                            );
                        }}
                    </For>
                </ul>
            </Show>
        </div>
    );
};

ClientSelector.parseProps = (atts: ClientsProps) => {
    return {
        clients: atts.clients ?? [],
        showTitles: atts.showTitles ?? true,
        animateOpacity: atts.animateOpacity ?? true,
        controlLayout: atts.controlLayout ?? {
            isControlled: false,
            rowLength: 3,
        },
        hideFilter: atts.hideFilter ?? false,
        clientCategoriesFromHook: atts.clientCategoriesFromHook ?? [],
    };
};
